import { supabase } from './supabase'
import { preparePhoto, type PreparedPhoto } from './photos'

/**
 * Pictures and page details on a PRIVATE question (116, HLP-26; David, 25 Sep).
 *
 * Chandra's reports were screenshots in Teams. David wants them as platform
 * tickets, so the routing step can carry up to three pictures — a file the
 * person picks (on a phone that is the photo picker, where their screenshots
 * already are) or a picture of the page the app draws itself — plus the page's
 * own details, attached automatically and SAID so before sending.
 *
 * ── THE BUCKET IS `support-media`, NEVER `post-media` ───────────────────────
 *
 * post-media is readable by every member of the tenant (019). A private
 * question has two readers (089). 116 adds a bucket whose read rule is the
 * thread's own, and a trigger that refuses a private question's picture
 * anywhere else, so a slip here fails loudly instead of leaking quietly.
 *
 * ── THE ORDER IS: QUESTION FIRST, THEN PICTURES ─────────────────────────────
 *
 * The opposite of the composer's (019 uploads, then creates the post), because
 * the path is `<tenant>/<question>/<uuid>.jpg` and the Storage policy is scoped
 * by the QUESTION — the thing that decides who may read it. A picture that
 * fails to upload after the question went does not un-send the question; the
 * person is told which part did not arrive (see `attachSupportPictures`).
 */

/** A ticket, not an album. The trigger in 116 says the same number. */
export const MAX_SUPPORT_PICTURES = 3

export type PageContext = {
  route: string
  viewport: string
  app_version: string
  locale: string
  browser: string
}

declare const __APP_BUILD__: string | undefined

/**
 * Where the person was. The five keys 116's allow-list keeps — anything else
 * sent would be dropped by the server, so nothing else is gathered here.
 *
 * Read when the routing step OPENS, not when the drawer does: the page behind
 * the drawer is still the page they were on, and a read-at-send would pick up
 * nothing different while being harder to show them in advance.
 */
export function readPageContext(locale: string): PageContext {
  const w = typeof window !== 'undefined' ? window : undefined
  return {
    route: w ? (w.location.hash || w.location.pathname || '/') : '',
    viewport: w ? `${w.innerWidth}×${w.innerHeight}` : '',
    app_version: typeof __APP_BUILD__ === 'string' && __APP_BUILD__ ? __APP_BUILD__ : 'unknown',
    locale,
    browser: typeof navigator !== 'undefined' ? navigator.userAgent : '',
  }
}

/**
 * A picture of the page behind the drawer, drawn by the app.
 *
 * It is DRAWN FROM THE PAGE'S OWN MARKUP (html-to-image), not screen-captured:
 * a real screen capture needs a browser permission prompt asking which screen
 * to share, and iPhones do not offer one at all. So embedded video comes out
 * blank and the result is what the app thinks it drew — which is why it is
 * shown as a thumbnail with Remove before anything sends, and why "attach a
 * screenshot" is the first control and this is the second.
 *
 * The drawer and its launcher are LEFT OUT: the person wants a picture of what
 * they were looking at, not of the question they are typing about it. Only the
 * visible window is drawn, at the scroll position they left it.
 *
 * Lazy-imported, so the library costs nothing to the ~930 people who never
 * press the button.
 */
export async function capturePagePicture(): Promise<File> {
  const { toBlob } = await import('html-to-image')
  const skip = (node: HTMLElement) =>
    !(node instanceof HTMLElement) ||
    !(node.classList?.contains('onedrawer') || node.classList?.contains('askbubble') || node.dataset?.noCapture === 'true')
  const blob = await toBlob(document.body, {
    width: window.innerWidth,
    height: window.innerHeight,
    pixelRatio: 1,
    cacheBust: false,
    style: { transform: `translate(${-window.scrollX}px, ${-window.scrollY}px)`, transformOrigin: 'top left' },
    filter: (n) => skip(n as HTMLElement),
  })
  if (!blob) throw new Error('The page could not be drawn.')
  return new File([blob], 'page.png', { type: 'image/png' })
}

/**
 * One picture, ready to show and send. `preparePhoto` is the ONE resize and
 * EXIF strip (photos.ts says why they are inseparable) — a phone screenshot
 * can carry location too, and a support ticket is not a reason to skip it.
 */
export async function prepareSupportPicture(file: File, fromPage = false): Promise<SupportPicture> {
  const p = await preparePhoto(file)
  return { ...p, fromPage }
}

export type SupportPicture = PreparedPhoto & {
  /** Drawn by the app, rather than chosen by the person. Shown as such. */
  fromPage: boolean
}

/**
 * Upload the pictures under the question and record them.
 *
 * Returns how many ARRIVED. Each is independent: a failure on the second does
 * not take the first down, and the caller says "the question went, one picture
 * did not" rather than pretending either all or none did.
 */
export async function attachSupportPictures(
  questionId: string,
  pictures: SupportPicture[],
): Promise<{ attached: number; failed: number }> {
  if (pictures.length === 0) return { attached: 0, failed: 0 }
  // The asker can read their own question row (034's author clause), so the
  // tenant comes from the row the server just wrote, never from a guess.
  const { data: row, error: rowError } = await supabase
    .from('content')
    .select('tenant_id')
    .eq('id', questionId)
    .single()
  if (rowError || !row) return { attached: 0, failed: pictures.length }
  const tenantId = (row as { tenant_id: string }).tenant_id

  let attached = 0
  let failed = 0
  for (const [i, p] of pictures.slice(0, MAX_SUPPORT_PICTURES).entries()) {
    const path = `${tenantId}/${questionId}/${crypto.randomUUID()}.jpg`
    try {
      const { error: upError } = await supabase.storage
        .from('support-media')
        .upload(path, p.file, { contentType: 'image/jpeg', upsert: false })
      if (upError) throw upError
      // Plain insert, no .select(): RETURNING on a table whose read policy
      // re-queries content is refused (OPS-23).
      const { error: rowErr } = await supabase.from('content_attachment').insert({
        tenant_id: tenantId,
        content_id: questionId,
        storage_bucket: 'support-media',
        storage_path: path,
        mime_type: 'image/jpeg',
        byte_size: p.file.size,
        width: p.width,
        height: p.height,
        alt_text: p.fromPage ? 'Picture of the page' : null,
        exif_stripped: true,
        position: i,
      })
      if (rowErr) throw rowErr
      attached++
    } catch {
      failed++
    }
  }
  return { attached, failed }
}

/** A displayable URL for a support picture. Private bucket, one hour (photos.ts). */
export async function signedSupportPictureUrl(path: string): Promise<string | null> {
  const { data, error } = await supabase.storage.from('support-media').createSignedUrl(path, 3600)
  if (error) return null
  return data?.signedUrl ?? null
}
